import React from 'react';
import Downshift from 'downshift';
import PropTypes from 'prop-types';
import Radio from '../form/radio/Radio';

const SearchField = (props) => {
    const {
        preSelectedFirstItem,
        onInputChange,
        onSubmit,
        onChange,
        itemStyle,
        items,
        itemComponent,
        className,
        inputPlaceholder,
        categories,
        suggestions,
        inputValue: initialInputValue,
        ...downshiftProps
    } = props;

    const [selected, setSelected] = React.useState(null);
    const searchInputRef = React.useRef(null);
    const hasCategories =
        categories &&
        Array.isArray(categories.items) &&
        categories.items.length > 0;
    const hasSuggestions =
        suggestions &&
        Array.isArray(suggestions.items) &&
        suggestions.items.length > 0;

    function stateReducer(state, changes) {
        // this prevents the menu from being closed when the user
        // selects an item with a keyboard or mouse
        switch (changes.type) {
            case Downshift.stateChangeTypes.mouseUp:
                // Preserve input value on mouseup (when clicking outside)
                return {
                    ...changes,
                    inputValue: state.inputValue
                };
            case Downshift.stateChangeTypes.blurInput:
                return {
                    ...changes,
                    ...(items[state.highlightedIndex] && {
                        inputValue: items[state.highlightedIndex].value
                    }),
                    ...(typeof onSubmit !== 'undefined' && {
                        inputValue: state.inputValue
                    }),
                    ...(onSubmit === 'undefined' && {
                        highlightedIndex: state.highlightedIndex
                    })
                };
            default:
                return changes;
        }
    }

    return (
        <form onSubmit={(e) => {
            const value = e.target.elements.q.value.trim();
            if (value.length < props.minQueryLength) {
                e.preventDefault();
                return;
            }
            onSubmit && onSubmit(e);
        }}>
            <Downshift
                itemToString={(item) => (item ? item.value : ``)}
                defaultHighlightedIndex={preSelectedFirstItem ? 0 : null}
                initialInputValue={initialInputValue}
                stateReducer={stateReducer}
                {...downshiftProps}
            >
                {({
                    getInputProps,
                    getItemProps,
                    getMenuProps,
                    isOpen,
                    inputValue,
                    highlightedIndex,
                    reset,
                    clearSelection
                }) => (
                    <div className={`search-field ${className}`}>
                        {/* Search field */}
                        <div className="search-field__field">
                            {/* Wrapper positions the clear button inside the input, regardless of buttons after it */}
                            <div className="search-field__input-wrapper">
                                <input
                                    name="q"
                                    className="search-field__input"
                                    placeholder={inputPlaceholder}
                                    type="search"
                                    ref={input => { searchInputRef.current = input; }}
                                    {...getInputProps({
                                        onChange: (e) => onInputChange(e),
                                        onKeyDown: (e) => {
                                            if (e.key === 'Enter' && highlightedIndex !== null && items[highlightedIndex]) {
                                                e.preventDefault();
                                                onChange && onChange(items[highlightedIndex]);
                                            }
                                        },
                                        // Ensure value is always a string to prevent controlled/uncontrolled warning
                                        value: inputValue || ''
                                    })}
                                />

                                {/* Clear button */}
                                {inputValue && inputValue.length > 0 && (
                                    <button
                                        type="button"
                                        className="search-field__clear"
                                        aria-label="Rensa sökfältet"
                                        onClick={(e) => {
                                            onInputChange({
                                                target: { value: '' }
                                            });

                                            clearSelection();
                                            reset();
                                            if (searchInputRef.current) {
                                                searchInputRef.current.focus();
                                            }
                                            e.preventDefault();
                                        }}
                                    />
                                )}
                            </div>

                            <button
                                className="search-field__submit"
                                type="submit"
                                onClick={(e) => {
                                    if (
                                        typeof items[highlightedIndex] !==
                                        'undefined'
                                    ) {
                                        onChange(items[highlightedIndex]);
                                        e.preventDefault();
                                    } else if (
                                        typeof onSubmit === 'undefined'
                                    ) {
                                        e.preventDefault();
                                    }
                                }}
                                aria-label={inputPlaceholder}
                            />
                            {props.children}
                        </div>
                        {/* Autocomplete */}
                        {isOpen && inputValue.length > 0 && items.length > 0 ? (
                            <React.Fragment>
                                <ul
                                    className={`search-field__autocomplete ${
                                        isOpen &&
                                        `search-field__autocomplete--is-open`
                                    }`}
                                    {...getMenuProps()}
                                >
                                    {items.map((item, index) => (
                                        <li
                                            className={`search-field__item ${
                                                highlightedIndex === index &&
                                                `search-field__item--selected`
                                            } ${itemStyle}`}
                                            {...getItemProps({
                                                key: index,
                                                index,
                                                item
                                            })}
                                        >
                                            {itemComponent(item)}
                                        </li>
                                    ))}
                                </ul>
                            </React.Fragment>
                        ) : null}
                    </div>
                )}
            </Downshift>

            {(hasCategories || hasSuggestions) && (
                <div className="row">
                    {/* Categories */}
                    {hasCategories && (
                        <div className="column-block col col--span-12 col--span-s-8">
                            <div className="search-field__categories">
                                <Radio
                                    id="search-category"
                                    name="category"
                                    label={categories.label}
                                    selectedValue={selected || categories.items[0].key}
                                    onChange={(val) =>
                                        setSelected(val)
                                    }
                                    modifier="radio-pill"
                                    options={categories.items}
                                />
                            </div>
                        </div>
                    )}
                    <div className="column-block col col--span-12 col--span-s-4">
                        {/* Popular links */}
                        {hasSuggestions && (
                            <div className="search-field__suggestions">
                                {suggestions.label && (
                                    <div className="search-field__suggestions-label">
                                        {suggestions.label}
                                    </div>
                                )}
                                <ul>
                                    {suggestions &&
                                        suggestions.items &&
                                        suggestions.items.map(
                                            (
                                                popularLink,
                                                index
                                            ) => {
                                                return (
                                                    <li key={index}>
                                                        <a
                                                            href={
                                                                popularLink.url
                                                            }
                                                        >
                                                            {
                                                                popularLink.label
                                                            }
                                                        </a>
                                                    </li>
                                                );
                                            }
                                        )}
                                </ul>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </form>
    );
};

SearchField.propTypes = {
    onInputChange: PropTypes.func,
    onSubmit: PropTypes.func,
    itemStyle: PropTypes.string,
    items: PropTypes.array.isRequired,
    itemComponent: PropTypes.func,
    className: PropTypes.string,
    inputPlaceholder: PropTypes.string,
    preSelectedFirstItem: PropTypes.bool,
    seeMoreLink: PropTypes.string,
    inputValue: PropTypes.string,
    categories: PropTypes.shape({
        label: PropTypes.string,
        items: PropTypes.arrayOf(
            PropTypes.shape({
                value: PropTypes.string,
                label: PropTypes.string
            })
        )
    }),
    suggestions: PropTypes.shape({
        label: PropTypes.string,
        items: PropTypes.arrayOf(
            PropTypes.shape({
                label: PropTypes.string,
                url: PropTypes.string
            })
        )
    }),
    minQueryLength: PropTypes.number
};

SearchField.defaultProps = {
    onInputChange: () => {},
    itemStyle: '',
    className: '',
    preSelectedFirstItem: true,
    seeMoreLink: ''
};

export default SearchField;
